import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { COURIER_KEYS } from '../lib';
import { CourierApi } from '../api';
import type { CourierBase, CourierCreatePayload, CourierUpdatePayload, CourierItem, CourierItemListParams } from './types';

export const useCourierList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<CourierBase>>({
    queryKey: COURIER_KEYS.list(params),
    queryFn: () => CourierApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useCourierItemList = (params?: CourierItemListParams) => {
  return useQuery<ApiResponse<CourierItem[]>, Error, CourierItem[] | undefined>({
    queryKey: COURIER_KEYS.itemList(params),
    queryFn: () => CourierApi.getItemList(params),
    enabled: params !== undefined,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useCourierById = (id: number) => {
  return useQuery<ApiResponse<CourierBase>>({
    queryKey: COURIER_KEYS.detail(id),
    queryFn: () => CourierApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateCourier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CourierCreatePayload) => CourierApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COURIER_KEYS.all });
    },
  });
};

export const useUpdateCourier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CourierUpdatePayload) => CourierApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COURIER_KEYS.all });
    },
  });
};

export const useDeleteCourier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => CourierApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COURIER_KEYS.all });
    },
  });
};
